// Encart « Pièces du dossier — exonération » (écran admin AnalyseCspe).
// - Joint au dossier signé : le détail du calcul du % exonéré (PDF, lié à l'année sélectionnée),
//   ajouté à la fin du dossier ERA + 3310 lors de la fusion (étape 6).
// - Archive (en cas de contrôle) : 4 pièces valables pour le compte, toutes années.
// Routes : /api/cspe/admin/accounts/:accountId/pieces-dossier (GET liste, POST upload-url,
// POST enregistrement, DELETE). Dépôt du fichier : uploadToSignedUrl sur le bucket privé
// « pieces-dossier ». Toute erreur (HTTP ou stockage) est lue, loguée et affichée en toast.
import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiPaperclip, FiArchive, FiUpload, FiTrash2, FiExternalLink, FiLoader, FiAlertTriangle, FiFileText } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";
import { confirmToast } from "../../lib/confirmToast";

// Même plafond que le backend (TAILLE_MAX_OCTETS, backend/src/utils/piecesDossier.js).
const TAILLE_MAX_OCTETS = 20 * 1024 * 1024;
const MIMES_PDF = ["application/pdf"];
const MIMES_ARCHIVE = ["application/pdf", "image/jpeg", "image/png"];

const CATEGORIE_CALCUL = {
  cle: "calcul_exoneration",
  libelle: "Détail du calcul du % de consommation exonérée",
  mimes: MIMES_PDF,
  accept: "application/pdf,.pdf",
  formats: "PDF",
};
const CATEGORIES_ARCHIVE = [
  { cle: "attestation_mes_grdf", libelle: "Attestation de mise en service GRDF" },
  { cle: "attestation_prefectorale", libelle: "Attestation préfectorale" },
  { cle: "contrat_achat_biomethane", libelle: "Contrat d'achat de biométhane" },
  { cle: "facture_vente_biomethane", libelle: "Factures de vente de biométhane" },
].map((c) => ({ ...c, mimes: MIMES_ARCHIVE, accept: "application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png", formats: "PDF, JPG ou PNG" }));

// Contrôle côté écran avant tout appel réseau ; renvoie un message ou null.
function verifierFichier(categorie, file) {
  if (!categorie.mimes.includes(file.type)) {
    return categorie.cle === CATEGORIE_CALCUL.cle
      ? `« ${file.name} » refusé : le détail du calcul doit être un PDF.`
      : `« ${file.name} » refusé : formats acceptés ${categorie.formats}.`;
  }
  if (!(file.size > 0)) return `« ${file.name} » est vide.`;
  if (file.size > TAILLE_MAX_OCTETS) return `« ${file.name} » dépasse 20 Mo (${(file.size / 1024 / 1024).toFixed(1)} Mo).`;
  return null;
}

// Appel authentifié ; lève une Error portant le message renvoyé par l'API si !response.ok.
// Les 4 routes renvoient toujours un objet JSON : une réponse OK au corps non JSON, nul ou
// non objet (page HTML d'un proxy, corps vide…) est une erreur, jamais une liste vide muette.
async function appelApi(url, { method = "GET", body } = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = { Authorization: `Bearer ${session?.access_token}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const response = await fetch(url, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined });
  let donnees = null;
  try { donnees = await response.json(); } catch { donnees = null; }
  if (!response.ok) throw new Error(donnees?.message || `Erreur HTTP ${response.status}`);
  if (donnees === null || typeof donnees !== "object" || Array.isArray(donnees)) {
    throw new Error("Réponse inattendue du serveur");
  }
  return donnees;
}

const formaterDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

export default function PiecesDossierExoneration({ accountId, fiscalYear, dossierCombine }) {
  const [pieces, setPieces] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [envoisEnCours, setEnvoisEnCours] = useState(() => new Set()); // catégories en cours d'envoi
  const [suppressionEnCours, setSuppressionEnCours] = useState(null); // id de pièce
  const [calculModifie, setCalculModifie] = useState(false);
  const inputs = useRef({});

  const base = `/api/cspe/admin/accounts/${accountId}/pieces-dossier`;
  // Clé du contexte affiché. Une réponse arrivée après un changement de compte ou d'année
  // (clé différente) est ignorée : elle ne doit pas écraser la liste de la nouvelle année.
  const cle = `${accountId}:${fiscalYear}`;
  const cleCourante = useRef(cle);

  // Changement de compte/année (y compris année vidée) : liste et bandeau remis à zéro.
  // Déclaré AVANT l'effet de chargement pour que la clé soit à jour quand il part.
  useEffect(() => {
    cleCourante.current = cle;
    setPieces([]);
    setCalculModifie(false);
    // Un chargement en vol pour l'ancienne clé ne remettra pas l'indicateur à false
    // (garde de clé) : on le fait ici, sinon le spinner reste bloqué (ex. année vidée).
    setChargement(false);
  }, [cle]);

  const charger = useCallback(async () => {
    if (!accountId || !fiscalYear) return;
    const cleLancement = `${accountId}:${fiscalYear}`;
    setChargement(true);
    try {
      const donnees = await appelApi(`${base}?fiscal_year=${fiscalYear}`);
      if (cleCourante.current !== cleLancement) return; // réponse périmée
      setPieces(Array.isArray(donnees?.pieces) ? donnees.pieces : []);
    } catch (e) {
      console.error("[PiecesDossierExoneration] lecture :", e);
      if (cleCourante.current === cleLancement) toast.error(e.message);
    } finally {
      if (cleCourante.current === cleLancement) setChargement(false);
    }
  }, [accountId, fiscalYear, base]);

  useEffect(() => { charger(); }, [charger]);

  const marquerEnvoi = (cleCategorie, actif) =>
    setEnvoisEnCours((prec) => {
      const suivant = new Set(prec);
      if (actif) suivant.add(cleCategorie); else suivant.delete(cleCategorie);
      return suivant;
    });

  const deposer = async (categorie, file) => {
    const refus = verifierFichier(categorie, file);
    if (refus) { toast.error(refus); return; }
    const cleLancement = cle;
    marquerEnvoi(categorie.cle, true);
    try {
      const meta = {
        categorie: categorie.cle,
        file_name: file.name,
        mime_type: file.type,
        size_bytes: file.size,
        fiscal_year: fiscalYear,
      };
      // 1. URL de dépôt signée
      const { path, token } = await appelApi(`${base}/upload-url`, { method: "POST", body: meta });
      // 2. Dépôt direct dans le bucket privé
      const { error } = await supabase.storage.from("pieces-dossier").uploadToSignedUrl(path, token, file, { contentType: file.type });
      if (error) throw new Error(`Dépôt du fichier : ${error.message || "échec"}`);
      // 3. Enregistrement de la pièce
      await appelApi(base, { method: "POST", body: { ...meta, path } });
      toast.success(`« ${file.name} » ajouté`);
      // Contexte changé pendant l'envoi : rien à mettre à jour dans l'écran courant.
      if (cleCourante.current !== cleLancement) return;
      if (categorie.cle === CATEGORIE_CALCUL.cle) setCalculModifie(true);
      await charger();
    } catch (e) {
      console.error("[PiecesDossierExoneration] dépôt :", e);
      toast.error(e.message);
    } finally {
      marquerEnvoi(categorie.cle, false);
      if (inputs.current[categorie.cle]) inputs.current[categorie.cle].value = "";
    }
  };

  const supprimer = async (piece) => {
    // Libellé propre (pas « Supprimer ») : distinct des boutons de la table des CERFA générés
    const ok = await confirmToast({
      title: `Supprimer « ${piece.file_name} » ?`,
      subtitle: "Le fichier sera définitivement effacé.",
      confirmLabel: "Supprimer la pièce",
    });
    if (!ok) return;
    const cleLancement = cle;
    setSuppressionEnCours(piece.id);
    try {
      await appelApi(`${base}/${piece.id}`, { method: "DELETE" });
      toast.success(`« ${piece.file_name} » supprimé`);
      if (cleCourante.current !== cleLancement) return;
      if (piece.categorie === CATEGORIE_CALCUL.cle) setCalculModifie(true);
      await charger();
    } catch (e) {
      console.error("[PiecesDossierExoneration] suppression :", e);
      toast.error(e.message);
    } finally {
      setSuppressionEnCours(null);
    }
  };

  const renderCategorie = (categorie) => {
    const liste = pieces.filter((p) => p.categorie === categorie.cle);
    const envoi = envoisEnCours.has(categorie.cle);
    return (
      <div key={categorie.cle} data-testid={`piece-cat-${categorie.cle}`} className="p-2 bg-white rounded border border-gray-200">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-medium text-slate-700 flex items-center min-w-0">
            <FiFileText className="h-3.5 w-3.5 mr-1 flex-shrink-0 text-slate-400" />
            <span className="truncate">{categorie.libelle}</span>
          </p>
          <label className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded border ${envoi ? "bg-gray-100 text-gray-400 border-gray-200 cursor-wait" : "cursor-pointer bg-white text-cyan-700 border-cyan-300 hover:bg-cyan-50"}`}>
            {envoi ? <FiLoader className="animate-spin h-3 w-3 mr-1" /> : <FiUpload className="h-3 w-3 mr-1" />}
            {envoi ? "Envoi…" : "Déposer"}
            <input
              type="file"
              className="hidden"
              accept={categorie.accept}
              disabled={envoi}
              data-testid={`input-piece-${categorie.cle}`}
              ref={(el) => { inputs.current[categorie.cle] = el; }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) deposer(categorie, f); }}
            />
          </label>
        </div>
        <p className="text-[10px] text-gray-400 mt-0.5">{categorie.formats} — 20 Mo max</p>
        {liste.length === 0 ? (
          <p className="text-[10px] text-gray-400 italic mt-1">Aucune pièce.</p>
        ) : (
          <ul className="mt-1 space-y-0.5">
            {liste.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-2 text-[10px]">
                <div className="min-w-0">
                  <span className="block truncate text-slate-700">{p.file_name}</span>
                  <span className="text-gray-400 ml-1">— {formaterDate(p.uploaded_at)}</span>
                  {!p.signed_url && (
                    <p className="text-red-600">{p.erreur_url || "Lien de consultation indisponible."}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {p.signed_url && (
                    <a href={p.signed_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-cyan-700 hover:underline">
                      <FiExternalLink className="h-3 w-3 mr-0.5" />Voir
                    </a>
                  )}
                  <button
                    type="button"
                    data-testid="supprimer-piece"
                    onClick={() => supprimer(p)}
                    disabled={suppressionEnCours === p.id}
                    className="inline-flex items-center text-red-600 hover:text-red-800 disabled:text-gray-400"
                    aria-label={`Supprimer ${p.file_name}`}
                  >
                    {suppressionEnCours === p.id ? <FiLoader className="animate-spin h-3 w-3" /> : <FiTrash2 className="h-3 w-3" />}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {/* Bloc joint au dossier : TOUS ses fichiers sont ajoutés à la fusion, dans l'ordre de dépôt */}
        {categorie.cle === CATEGORIE_CALCUL.cle && liste.length >= 2 && (
          <p data-testid="avertissement-plusieurs-calculs" className="mt-1 text-[10px] text-amber-700">
            {liste.length} fichiers seront joints au dossier, dans l'ordre de dépôt. Supprimez l'ancienne version si besoin.
          </p>
        )}
      </div>
    );
  };

  return (
    <div data-testid="pieces-dossier-exoneration" className="p-3 bg-gray-50 rounded border border-gray-200 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-800 flex items-center">
          <FiPaperclip className="h-4 w-4 mr-1.5 text-slate-500" />
          Pièces du dossier — exonération
        </h3>
        {chargement && <FiLoader className="animate-spin h-3.5 w-3.5 text-gray-400" aria-label="Chargement" />}
      </div>

      {!fiscalYear && (
        <p className="text-[10px] text-gray-500">Sélectionnez une année pour afficher les pièces.</p>
      )}

      {dossierCombine && calculModifie && (
        <div data-testid="bandeau-recombiner" className="flex items-start gap-1.5 p-2 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-800">
          <FiAlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
          <span>Le dossier de l'année est déjà combiné : si la signature n'a pas encore été envoyée, supprimez-le (étape 6) puis recombinez pour inclure ce changement. Si elle a été envoyée, contactez Adrien.</span>
        </div>
      )}

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Joint au dossier signé{fiscalYear ? ` — ${fiscalYear}` : ""}</p>
        <p className="text-[10px] text-gray-500 mb-1.5">Ajouté à la fin du dossier ERA + 3310 lors de la fusion (étape 6), signé par le client via Yousign.</p>
        {renderCategorie(CATEGORIE_CALCUL)}
      </div>

      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 flex items-center">
          <FiArchive className="h-3 w-3 mr-1" />Archive (en cas de contrôle) — toutes années
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {CATEGORIES_ARCHIVE.map(renderCategorie)}
        </div>
      </div>
    </div>
  );
}
